/** @jsx jsx */
import React, {
	useCallback,
	useMemo,
	useState,
	type PropsWithChildren,
	type SyntheticEvent,
} from 'react';
import { cssMap, jsx } from '@compiled/react';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';
import { JiraSpotlight } from '@atlassian/jira-spotlight/src/ui/jira-spotlight.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { useIsTreeViewEnabled } from '@jira/polaris__lib-entitlement-utils/src/useIsTreeViewEnabled.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import treeViewOnboardingImageLight from '@jira/illustrations__illustrations/src/ui/adg4/jira/spots/software/assets/jpd-treeview-spotlight-image.svg';
import treeViewOnboardingImageDark from '@jira/illustrations__illustrations/src/ui/adg4/jira/spots/software/assets/jpd-treeview-spotlight-image-dark.svg';
import { SpotlightTarget } from '@atlassian/jira-spotlight/src/ui/SpotlightTarget.tsx';
import {
	OnboardingSpotlightFeatureIds,
	PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	PO_HEADLESS_PLACEMENT_ID,
} from '../../../onboardingConstants.tsx';
import { useOnboardingFlowsActions } from '../../../controllers/index.tsx';
import { useTreeViewOnboardingSpotlightDismissed } from '../../../controllers/selectors/useTreeViewOnboardingSpotlightDismissed.tsx';
import { TREE_VIEW_ONBOARDING_SPOTLIGHT } from '../../../controllers/constants.tsx';
import { usePolarisHeadlessSpotlightMessage } from '../../../usePolarisHeadlessSpotlightMessage.tsx';

const styles = cssMap({
	pulseOutline: {
		position: 'relative',
		zIndex: 1,
		outlineStyle: 'solid',
		outlineWidth: token('border.width.selected'),
		outlineColor: token('color.background.neutral.bold'),
		outlineOffset: token('space.0'),
	},
});

const spotlightHeading = defineMessage({
	id: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.heading',
	defaultMessage: 'Explore the new Tree view',
	description: 'Heading for the Tree view onboarding spotlight',
});

const spotlightDescription = defineMessage({
	id: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.description',
	defaultMessage: 'Connect customer problems to solutions and experiments in a visual hierarchy.',
	description: 'Description for the Tree view onboarding spotlight',
});

const spotlightCreateButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.create',
	defaultMessage: 'Create',
	description: 'Create button label for the Tree view onboarding spotlight - creates a tree view',
});

const spotlightNotNowButton = defineMessage({
	id: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.not-now',
	defaultMessage: 'Not now',
	description: 'Dismiss button label for the Tree view onboarding spotlight',
});

type TreeViewOnboardingSpotlightProps = PropsWithChildren<{
	isNonProjectContainer?: boolean;
	onCreateTreeView?: () => void;
}>;

const TreeViewOnboardingSpotlightOld = ({
	children,
	isNonProjectContainer,
	onCreateTreeView,
}: TreeViewOnboardingSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const treeViewOnboardingImage =
		colorMode === 'dark' ? treeViewOnboardingImageDark : treeViewOnboardingImageLight;
	const isTreeViewEnabled = useIsTreeViewEnabled();

	/* eslint-disable jira/ff/no-preconditioning -- jpd_tree-view controls feature availability; jpd_tree_view_onboarding controls onboarding rollout. Both must be true and cannot be combined in Statsig. */
	const canHaveSpotlight =
		!isNonProjectContainer &&
		isTreeViewEnabled &&
		fg('jpd_tree-view') &&
		fg('jpd_tree_view_onboarding');
	/* eslint-enable jira/ff/no-preconditioning */

	// Tracks whether the pulse outline should be hidden for this session.
	// Set by spotlight buttons (persisted) or X/outside-click (session-only).
	const [isLocallyDismissed, setIsLocallyDismissed] = useState(false);

	const isSpotlightDismissed = useTreeViewOnboardingSpotlightDismissed();
	const { loadTreeViewOnboardingSpotlightDismissed, setTreeViewOnboardingSpotlightDismissed } =
		useOnboardingFlowsActions();

	useEffectWithoutConcurrency(() => {
		if (!canHaveSpotlight) {
			return;
		}

		loadTreeViewOnboardingSpotlightDismissed();
	}, [loadTreeViewOnboardingSpotlightDismissed, canHaveSpotlight]);

	const handleCreate = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'treeViewOnboardingSpotlightCreate');
			setIsLocallyDismissed(true);
			setTreeViewOnboardingSpotlightDismissed();
			onCreateTreeView?.();
		},
		[setTreeViewOnboardingSpotlightDismissed, onCreateTreeView],
	);

	const handleDismiss = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'treeViewOnboardingSpotlightDismiss');
			setIsLocallyDismissed(true);
			setTreeViewOnboardingSpotlightDismissed();
		},
		[setTreeViewOnboardingSpotlightDismissed],
	);

	const handleSpotlightDismiss = useCallback(() => {
		setIsLocallyDismissed(true);
		setTreeViewOnboardingSpotlightDismissed();
	}, [setTreeViewOnboardingSpotlightDismissed]);

	const actions = useMemo(
		() => [
			{
				onClick: handleCreate,
				text: formatMessage(spotlightCreateButton),
				ctaType: 'primary' as const,
				testId: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.create-button',
			},
			{
				onClick: handleDismiss,
				text: formatMessage(spotlightNotNowButton),
				ctaType: 'secondary' as const,
				appearance: 'subtle' as const,
				testId: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.dismiss-button',
			},
		],
		[handleCreate, handleDismiss, formatMessage],
	);

	// Don't show the spotlight if gate is off, non-project container, or dismissed (or not yet loaded)
	const shouldShowSpotlight = canHaveSpotlight && isSpotlightDismissed === false;

	if (!shouldShowSpotlight) {
		return <>{children}</>;
	}

	return (
		<>
			<SpotlightTarget name={TREE_VIEW_ONBOARDING_SPOTLIGHT} isFullWidth>
				<div css={!isLocallyDismissed && styles.pulseOutline}>{children}</div>
			</SpotlightTarget>
			<JiraSpotlight
				actions={actions}
				image={treeViewOnboardingImage}
				dialogPlacement="right bottom"
				dialogWidth={275}
				target={TREE_VIEW_ONBOARDING_SPOTLIGHT}
				heading={formatMessage(spotlightHeading)}
				targetRadius={3}
				targetBgColor={token('elevation.surface')}
				key={TREE_VIEW_ONBOARDING_SPOTLIGHT}
				messageId="lib-onboarding-flows.ui.tree-view-onboarding.spotlights.jira-spotlight"
				messageType="engagement"
				testId="lib-onboarding-flows.ui.tree-view-onboarding.spotlights.spotlights"
				isMigrated
				onDismiss={handleSpotlightDismiss}
			>
				{formatMessage(spotlightDescription)}
			</JiraSpotlight>
		</>
	);
};

const TreeViewOnboardingSpotlightNew = ({
	children,
	isNonProjectContainer,
	onCreateTreeView,
}: TreeViewOnboardingSpotlightProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const treeViewOnboardingImage =
		colorMode === 'dark' ? treeViewOnboardingImageDark : treeViewOnboardingImageLight;
	const isTreeViewEnabled = useIsTreeViewEnabled();
	const { dismiss, renderJiraSpotlight } = usePolarisHeadlessSpotlightMessage({
		placementId: PO_HEADLESS_PLACEMENT_ID,
		messageTemplateId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
		featureId: OnboardingSpotlightFeatureIds.TREE_VIEW_CREATE_MENU,
		clickIdentifiers: {
			dismiss: 'not-now',
			primary: 'create',
		},
	});

	/* eslint-disable jira/ff/no-preconditioning -- jpd_tree-view controls feature availability; jpd_tree_view_onboarding controls onboarding rollout. Both must be true and cannot be combined in Statsig. */
	const canHaveSpotlight =
		!isNonProjectContainer &&
		isTreeViewEnabled &&
		fg('jpd_tree-view') &&
		fg('jpd_tree_view_onboarding');
	/* eslint-enable jira/ff/no-preconditioning */

	const [isLocallyDismissed, setIsLocallyDismissed] = useState(false);

	const handleCreate = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'treeViewOnboardingSpotlightCreate');
			setIsLocallyDismissed(true);
			dismiss('primary');
			onCreateTreeView?.();
		},
		[dismiss, onCreateTreeView],
	);

	const handleDismiss = useCallback(
		(_event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'treeViewOnboardingSpotlightDismiss');
			setIsLocallyDismissed(true);
			dismiss('dismiss');
		},
		[dismiss],
	);

	const handleSpotlightDismiss = useCallback(() => {
		setIsLocallyDismissed(true);
		dismiss('dismiss');
	}, [dismiss]);

	const actions = useMemo(
		() => [
			{
				onClick: handleCreate,
				text: formatMessage(spotlightCreateButton),
				ctaType: 'primary' as const,
				testId: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.create-button',
			},
			{
				onClick: handleDismiss,
				text: formatMessage(spotlightNotNowButton),
				ctaType: 'secondary' as const,
				appearance: 'subtle' as const,
				testId: 'polaris.lib-onboarding-flows.tree-view-onboarding.spotlights.dismiss-button',
			},
		],
		[handleCreate, handleDismiss, formatMessage],
	);

	const spotlightContent = renderJiraSpotlight({
		actions,
		image: treeViewOnboardingImage,
		dialogPlacement: 'right bottom',
		target: TREE_VIEW_ONBOARDING_SPOTLIGHT,
		heading: formatMessage(spotlightHeading),
		testId: 'lib-onboarding-flows.ui.tree-view-onboarding.spotlights.spotlights',
		isMigrated: true,
		isVisible: canHaveSpotlight,
		onDismiss: handleSpotlightDismiss,
		children: formatMessage(spotlightDescription),
		fallbackMessageId: PO_HEADLESS_MESSAGE_TEMPLATE_ID,
	});

	if (!spotlightContent) {
		return <>{children}</>;
	}

	return (
		<>
			<SpotlightTarget name={TREE_VIEW_ONBOARDING_SPOTLIGHT} isFullWidth>
				<div css={!isLocallyDismissed && styles.pulseOutline}>{children}</div>
			</SpotlightTarget>
			{spotlightContent}
		</>
	);
};

export const TreeViewOnboardingSpotlight: React.FC<TreeViewOnboardingSpotlightProps> =
	componentWithFG(
		'post-office_jpd-juno_headless-spotlights-migration',
		TreeViewOnboardingSpotlightNew,
		TreeViewOnboardingSpotlightOld,
	);
